import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import NormalText from 'common/Typography/NormalText'
import TextColorFontHeader from 'common/Typography/TextColorFontHeader'
import addMonths from 'date-fns/addMonths'
import format from 'date-fns/format'
import isWithinInterval from 'date-fns/isWithinInterval'
import { generateColumnHeader } from 'helpers/column-generator/generateColumnHeader'
import { DataCDROverviewType } from 'types/api/faber/dataUsage.type'
import { TransformedGlobalUserCdrData } from 'types/api/faber/globalCustomers.type'

import CustomerUsageCDRBreakdown from './CustomerUsageCDRBreakdown'
function CDRLogsV3({ iccid, faberToken, customerRow, expanded }: CDRLogsProps) {
  const [cycleInterval, setCycleInterval] = useState('')
  const [isBreakdownDialogOpen, setIsBreakdownDialogOpen] = useState(false)

  const handleBreakdownBtnClick = () => {
    setIsBreakdownDialogOpen(true)
  }
  const handleBreakdownDialogClose = () => {
    setIsBreakdownDialogOpen(false)
  }

  /*FOR DATA GRID */
  const CDRTableColumns = useMemo(() => generateColumnHeader('dataUsage'), [])
  const CDRCustomerUsageTableColumns = useMemo(
    () => generateColumnHeader('customerDataUsage'),
    []
  )
  const [page, setPage] = useState(0)

  const [remainingData, setRemainingData] = useState('')
  const [overviewRows, setOverviewRows] = useState<DataCDROverviewType[]>([])
  const [customerUsageRows, setCustomerUsageRows] = useState<
    DataCDROverviewType[]
  >([])

  /*API CALL */
  const { data: overviewRowsData, isLoading: isOverviewCRDDataLoading } =
    useQuery(
      ['getOverviewCRDData', iccid],
      async () => {
        const getCDROverviewDataResponse = await DataApi.getDataCDROverview(
          iccid,
          faberToken
        )
        return getCDROverviewDataResponse.data
      },
      {
        onSuccess: (res) => {
          if (res.data) {
            const remaining = res.totalDataAllocatedMB - res.totalDataUsedMB
            setRemainingData(remaining.toFixed(2).toString())
            const filteredRows = res.data.filter(
              (row) => row.Type !== 'Customer Usage'
            )
            const customerUsageFilteredRows = res.data.filter(
              (row) => row.Type === 'Customer Usage'
            )
            setOverviewRows(filteredRows)
            setCustomerUsageRows(customerUsageFilteredRows)
          } else {
            setOverviewRows([])
          }
        },
        onError: () => {
          setOverviewRows([])
          toast.error('Error getting CDR data')
        },
        enabled: expanded === 'panel1' && !!iccid
      }
    )
  const allMonths = useMemo(() => {
    if (customerRow.subscriptionStartAt) {
      const months = [new Date(customerRow.subscriptionStartAt)]
      for (let i = 0; i < Number(customerRow.cycleCount); i += 1) {
        const lastMonth = months[months.length - 1]
        months.push(addMonths(lastMonth, 1))
      }

      return months
    } else return []
  }, [customerRow.subscriptionStartAt])

  useEffect(() => {
    if (cycleInterval && overviewRowsData?.data) {
      const selectedDatesRows = overviewRowsData?.data.filter((row) => {
        return (
          isWithinInterval(new Date(row.Timestamp), {
            start: new Date(cycleInterval),
            end: addMonths(new Date(cycleInterval), 1)
          }) && row.Type !== 'Customer Usage'
        )
      })

      setOverviewRows(selectedDatesRows)
    } else if (overviewRowsData?.data) {
      const filteredRows = overviewRowsData.data.filter(
        (row) => row.Type !== 'Customer Usage'
      )
      const customerUsageFilteredRows = overviewRowsData.data.filter(
        (row) => row.Type === 'Customer Usage'
      )

      setOverviewRows(filteredRows)
      setCustomerUsageRows(customerUsageFilteredRows)
    }
  }, [cycleInterval, overviewRowsData?.data])

  const handleCycleDateChange = (event) => {
    setCycleInterval(event.target.value as string)
  }

  const allDataFields = useMemo(() => {
    return {
      title: 'Data Allocation',
      fields: [
        {
          fieldName: 'Cycle Start Date',
          fieldValue: customerRow.subscriptionStartAt
            ? format(
                addMonths(
                  new Date(customerRow.subscriptionStartAt),
                  Number(customerRow.cycleCount) - 1
                ),
                'dd MMM yyyy HH:mm'
              )
            : ''
        },
        {
          fieldName: 'Current Plan',
          fieldValue: customerRow.planName
        },
        {
          fieldName: 'Total Data Allocated (MB)',
          fieldValue: overviewRowsData?.totalDataAllocatedMB
            ? overviewRowsData?.totalDataAllocatedMB.toFixed(2).toString()
            : ''
        },

        {
          fieldName: 'Total Data Usage (MB)',
          fieldValue: overviewRowsData?.totalDataAllocatedMB
            ? overviewRowsData?.totalDataUsedMB.toFixed(2).toString()
            : ''
        },
        {
          fieldName: 'Total Data Remaining (MB)',
          fieldValue: overviewRowsData
            ? (
                overviewRowsData.totalDataAllocatedMB -
                overviewRowsData.totalDataUsedMB
              )
                .toFixed(2)
                .toString()
            : ''
        }
      ]
    }
  }, [customerRow, overviewRowsData, remainingData])

  return (
    <Box>
      {isOverviewCRDDataLoading ? null : (
        <>
          <ProfileListInfo
            title={allDataFields.title}
            fields={allDataFields.fields.map((item) => ({
              fieldName: item.fieldName,
              fieldValue: item.fieldValue || ''
            }))}
          />

          <Divider sx={{ width: '100%', mb: 4 }} />
          <Stack direction='row' justifyContent='space-between'>
            <TextColorFontHeader>
              Data Package Provisioned Logs
            </TextColorFontHeader>
            <Box
              sx={{
                width: '25%',
                mb: 2
              }}
            >
              <FormControl fullWidth>
                <InputLabel
                  id='demo-simple-select-label'
                  sx={{ fontSize: '0.8em' }}
                >
                  Cycle Date
                </InputLabel>
                <Select
                  size='small'
                  sx={{
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 6px rgb(32 33 36 / 5%)'
                  }}
                  inputProps={{
                    sx: {
                      fontSize: '12px'
                    }
                  }}
                  defaultValue=''
                  label='Cycle Date'
                  labelId='demo-simple-select-label'
                  id='demo-simple-select'
                  value={cycleInterval}
                  onChange={handleCycleDateChange}
                  endAdornment={
                    cycleInterval ? (
                      <InputAdornment position='end' sx={{ mr: 2 }}>
                        <IconButton
                          onClick={() => {
                            setCycleInterval('')
                          }}
                        >
                          <HighlightOffIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                >
                  {allMonths.map(
                    (month, index) =>
                      index !== allMonths.length - 1 && (
                        <MenuItem
                          key={`month-${index}`}
                          value={format(month, 'd MMM yyyy')}
                          sx={{ fontSize: '12px' }}
                        >
                          {`${format(month, 'd MMM yyyy')} - ${format(
                            addMonths(month, 1),
                            'd MMM yyyy'
                          )}`}
                        </MenuItem>
                      )
                  )}
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <CustomerUsageCDRBreakdown
            handleDialogClose={handleBreakdownDialogClose}
            breakdownDialogOpen={isBreakdownDialogOpen}
            faberToken={faberToken}
            customerICCID={iccid}
          />
        </>
      )}
      <MUIDataGrid
        loading={isOverviewCRDDataLoading}
        getRowId={(row) => row.Timestamp + row.data + overviewRows.indexOf(row)}
        columns={CDRTableColumns}
        rows={overviewRows}
        tableHeight='300px'
        rowHeight={40}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        pagination
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        pageSize={5}
        sx={{
          '& .MuiDataGrid-iconSeparator': {
            visibility: 'hidden'
          },
          '& .MuiDataGrid-footerContainer': {
            border: '0'
          },
          '& .MuiDataGrid-cell': {
            border: '0px'
          },
          '& .MuiDataGrid-columnHeaders': {
            border: '0px'
          },
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
            outline: 'none'
          },
          mx: '-10px'
        }}
      />
      <Divider sx={{ width: '100%', my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextColorFontHeader>
          Data Usage Logs (Customer Usage)
        </TextColorFontHeader>
        <Button
          onClick={handleBreakdownBtnClick}
          startIcon={<VisibilityIcon />}
          sx={{ backgroundColor: '#ebedfb', border: 'none', px: 2 }}
        >
          <NormalText isBold sx={{ color: 'primary.main', fontSize: '0.8em' }}>
            View More
          </NormalText>
        </Button>
      </Box>
      <MUIDataGrid
        loading={isOverviewCRDDataLoading}
        getRowId={(row) => row.Timestamp + row.data + overviewRows.indexOf(row)}
        columns={CDRCustomerUsageTableColumns}
        rows={customerUsageRows}
        tableHeight='300px'
        rowHeight={40}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        pagination
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        pageSize={5}
        sx={{
          '& .MuiDataGrid-iconSeparator': {
            visibility: 'hidden'
          },
          '& .MuiDataGrid-footerContainer': {
            border: '0'
          },
          '& .MuiDataGrid-cell': {
            border: '0px'
          },
          '& .MuiDataGrid-columnHeaders': {
            border: '0px'
          },
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
            outline: 'none'
          },
          mx: '-10px'
        }}
      />
    </Box>
  )
}

export default CDRLogsV3

interface CDRLogsProps {
  faberToken: string
  customerRow: TransformedGlobalUserCdrData
  expanded: string | false
  iccid: string
}
